import { Injectable } from '@angular/core';
import { Router, CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Helper } from '@core/util/helper';

import { AuthenticationService } from 'app/authentication/service';
import { Permission } from 'app/shared/models';

interface PermissionRoute {
  permissions: string[];
  route: string;
}

/**
 * Ordered list of permission → route mappings used for fallback navigation.
 * The guard walks this list top-to-bottom and picks the first route whose
 * permission the user holds. Keep the order in sync with sidebar priority.
 */
const PERMISSION_ROUTE_MAP: PermissionRoute[] = [
  { permissions: [Permission.DashboardAdmin], route: '/dashboard/dashboard-admin' },
  { permissions: [Permission.DashboardProducer], route: '/dashboard/dashboard-producer' },
  { permissions: [Permission.SKUCatalogueAdmin], route: '/product-catalogue/product-catalogue-list' },
  { permissions: [Permission.OfferAdmin, Permission.UserRewardAdmin], route: '/reward-inventory/reward-inventory-list' },
  { permissions: [Permission.OfferAdmin], route: '/offer/offer-wizard-form' },
  { permissions: [Permission.ProductAdmin], route: '/products/product-list' },
  { permissions: [Permission.ReceiptAdmin, Permission.ReceiptApprover], route: '/receipt/receipt-list' },
  { permissions: [Permission.ConsumerAdmin], route: '/consumer/consumer-list' },
  { permissions: [Permission.NewsAdmin], route: '/news/news-list' },
  { permissions: [Permission.BannerAdmin], route: '/banner/banner-list' },
  { permissions: [Permission.SpinAdmin], route: '/spin/spin-question' },
  { permissions: [Permission.ChatConversationAdmin], route: '/chat/conversation' },
  { permissions: [Permission.ReportedChatAdmin], route: '/chat/reported-chat' },
  { permissions: [Permission.VoucherAdmin], route: '/voucher/voucher-list' },
  { permissions: [Permission.TargetedMarketingAdmin], route: '/targeted-marketing/targeted-marketing-list' },
  { permissions: [Permission.AuditLogAdmin], route: '/audit-log/audit-log-list' },
  { permissions: [Permission.DeliveryListAdmin], route: '/delivery/delivery-list' },
  { permissions: [Permission.DeliveryAdmin], route: '/delivery/pending-delivery-rewards-list' },
  { permissions: [Permission.DeliveryMethodAdmin], route: '/delivery-method' },
  { permissions: [Permission.HobbyInterestCategoryAdmin], route: '/hobbies-interest/hobby-interest-category-list' },
  { permissions: [Permission.OpenAIPromptAdmin], route: '/aieditor/openai' },
  { permissions: [Permission.PartnerAdmin], route: '/partners/partner' },
  { permissions: [Permission.StoreAdmin], route: '/partners/staff' },
  { permissions: [Permission.ConfigBrandMainAdmin, Permission.ConfigStoreMainAdmin], route: '/' },
  { permissions: [Permission.PartnerAdmin], route: '/system-settings' },
];

const CHANGE_PASSWORD_PATH = '/change-password/change-password-form';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
  constructor(
    private _router: Router,
    private _authenticationService: AuthenticationService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const currentUser = this._authenticationService.currentUserValue;

    if (!currentUser) {
      this._router.navigate(['/authentication/login'], { queryParams: { returnUrl: state.url } });
      return false;
    }

    const targetUrl = state.url.split('?')[0];
    const userPermissions = Helper.effectiveUserPermissions(currentUser);

    if (this._authenticationService.requiresPasswordChange(currentUser)) {
      if (targetUrl === CHANGE_PASSWORD_PATH) {
        return true;
      }
      this._router.navigate([CHANGE_PASSWORD_PATH]);
      return false;
    }

    if (this.isBlockedRoute(currentUser, targetUrl)) {
      this.navigateToFirstAccessible(currentUser, userPermissions, targetUrl);
      return false;
    }

    const routePermissions: string[] | null = route.data?.['permissions'] || route.firstChild?.data?.['permissions'] || null;

    if (!routePermissions) {
      return true;
    }

    const hasAccess = routePermissions.some((p) => userPermissions.includes(p));
    if (hasAccess) {
      return true;
    }

    this.navigateToFirstAccessible(currentUser, userPermissions, targetUrl);
    return false;
  }

  /**
   * Find the first route the user can access (that isn't the one being blocked)
   * and navigate there. If nothing is accessible, log out.
   */
  private navigateToFirstAccessible(currentUser: any, userPermissions: string[], blockedUrl: string): void {
    const target = AuthGuard.findFirstAccessibleRoute(currentUser, userPermissions, blockedUrl);
    if (target) {
      this._router.navigate([target]);
    } else {
      this._authenticationService.logout();
    }
  }

  /**
   * Returns the first route from PERMISSION_ROUTE_MAP the user can access,
   * skipping `excludeUrl` to prevent redirect loops, and skipping routes
   * that are blocked for this user type.
   */
  static findFirstAccessibleRoute(currentUser: any, userPermissions?: string[], excludeUrl?: string): string | null {
    const perms = userPermissions || Helper.effectiveUserPermissions(currentUser);
    const exclude = excludeUrl?.split('?')[0];

    for (const entry of PERMISSION_ROUTE_MAP) {
      if (exclude && entry.route === exclude) {
        continue;
      }
      if (!entry.permissions.some((p) => perms.includes(p))) {
        continue;
      }
      if (AuthGuard.isBlockedRouteForUser(currentUser, entry.route)) {
        continue;
      }
      return entry.route;
    }
    return null;
  }

  /**
   * Checks whether a route is blocked for the given user type
   * (staff can't access partner/staff pages; admin partners can't access staff page).
   */
  private isBlockedRoute(currentUser: any, url: string): boolean {
    return AuthGuard.isBlockedRouteForUser(currentUser, url);
  }

  private static isBlockedRouteForUser(currentUser: any, url: string): boolean {
    const path = url.split('?')[0];

    if (currentUser.loginType === 'staff') {
      if (path === '/partners/partner' || path.startsWith('/partners/partner/') || path === '/partners/staff' || path.startsWith('/partners/staff/')) {
        return true;
      }
    }

    if (currentUser.loginType !== 'staff' && currentUser.partnerType === 'admin' && (path === '/partners/staff' || path.startsWith('/partners/staff/'))) {
      return true;
    }

    return false;
  }
}
